import type { ComposerAttachment } from "@design";
import { useToast } from "@design";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  type AttachmentStatusItem,
  chatUploadAttachmentsMutation,
  errorMessage,
  getAttachmentLimitsOptions,
  type SharePointItem,
} from "@/api";
import { useCapabilities } from "@/features/account/useCurrentUser";
import { formatList } from "@/i18n/format";
import { acceptAttribute, batches, checkAttachments, rejectionFromError } from "./attachmentRules";
import { type CloudDoc, SharePointFolderLink } from "./cloudFiles";
import type { SentAttachment } from "./MessageAttachments";
import { uploadInRequests } from "./multipartQueue";
import { checkOf, type FileCheck, useFileChecks } from "./useFileChecks";
import { useRejectionToast } from "./useRejectionToast";

interface PendingAttachment {
  key: string;
  name: string;
  mimeType: string;
  /** The upload batch it came in (its AbortController in `batches`). */
  batch?: number;
  /** Uploaded file: set once uploaded. */
  uuid?: string;
  /** Cloud file (SharePoint/OneDrive/Drive): content extracted on attach. */
  cloud?: boolean;
  doc?: CloudDoc;
}

/** A SharePoint/OneDrive/Google Drive file to attach; `load` extracts its content. */
export interface CloudFile {
  name: string;
  mimeType?: string | null;
  load: () => Promise<CloudDoc>;
}

/** A pasted link that was a folder, waiting for the person to choose its files (one folder at a time). */
export interface FolderLink {
  key: string;
  folder: SharePointItem;
  /** How many more files this message can take. */
  room: number;
  /** The most files a message takes (`maxFiles`). */
  max: number;
  done: () => void;
}

const DEFAULT_MAX_FILES = 10;

let nextKey = 0;
let nextBatch = 0;

/** Why a file can't be used, by the backend's stable reason code. */
const REASON_KEYS = {
  MALWARE_DETECTED: "reasons.malware",
  TYPE_MISMATCH: "reasons.type",
  TYPE_NOT_ALLOWED: "reasons.type",
  ENCRYPTED_DOCUMENT: "reasons.encrypted",
  MACRO_DOCUMENT: "reasons.macros",
  CORRUPT_FILE: "reasons.damaged",
  EMPTY_FILE: "reasons.damaged",
} as const;

const reasonKey = (status: AttachmentStatusItem) =>
  REASON_KEYS[(status.reasonCode ?? "") as keyof typeof REASON_KEYS] ?? "reasons.other";

/**
 * Everything attached to the next message. Local files upload on attach,
 * which needs a chat, so the first one in a new chat creates it
 * (`ensureSession`). Files that exceed the backend's size/type limits are explained and left out.
 * Cloud files have their content extracted on attach and
 * travel inline with the message. Sending waits until everything is ready.
 *
 * Uploads are queued one request at a time (MHS-646). Once the backend
 * enforces attachment budgets, a selection larger than one multipart request
 * goes over several bounded requests; each request's files appear as it
 * finishes, and a failed request drops only its own files.
 *
 * With the file pipeline, uploaded files are checked (virus scan, type) before
 * the assistant can read them, so sending also waits for that. A file that
 * fails the check is removed from the message, with a toast saying why.

 */
export function useAttachments(ensureSession: () => Promise<string>) {
  const { t } = useTranslation("chat", { keyPrefix: "attachments" });
  const toast = useToast();
  const [items, setItems] = useState<PendingAttachment[]>([]);
  // Pasted folder links, offered one at a time
  const [folders, setFolders] = useState<Array<{ key: string; folder: SharePointItem }>>([]);
  const upload = useMutation(chatUploadAttachmentsMutation());
  // The chat file feature (MHS-646) also enforces per-turn budgets, so a large selection is split over requests
  const { chatFilePipelineEnabled } = useCapabilities();
  // One upload request at a time, across every attach in this composer.
  const queue = useRef<Promise<void>>(Promise.resolve());
  // The backend's limits rarely change: load once per app session.
  const { data: limits } = useQuery({ ...getAttachmentLimitsOptions(), staleTime: Number.POSITIVE_INFINITY });
  const explain = useRejectionToast(limits);

  const update = (key: string, change: Partial<PendingAttachment>) =>
    setItems((prev) => prev.map((item) => (item.key === key ? { ...item, ...change } : item)));
  const drop = (keys: string[]) => setItems((prev) => prev.filter((item) => !keys.includes(item.key)));
  // In-flight upload batches: unmounting, or removing the last of a batch's files, stops its requests
  const inFlight = useRef(new Map<number, AbortController>());
  useEffect(() => {
    const controllers = inFlight.current;
    return () => {
      for (const controller of controllers.values()) controller.abort();
    };
  }, []);
  // Removed files stay out of every later request of their batch; a batch with nothing left stops altogether
  const removed = useRef(new Set<string>());
  const remove = (key: string) => {
    removed.current.add(key);
    const batch = items.find((item) => item.key === key)?.batch;
    drop([key]);
    if (batch !== undefined && !items.some((item) => item.key !== key && item.batch === batch))
      inFlight.current.get(batch)?.abort();
  };
  // Each unusable file is reported once, however often its status is read again
  const reported = useRef(new Set<string>());

  const reportUploadError = (error: unknown, files: File[]) => {
    const rejection = rejectionFromError(error, files);
    if (rejection) {
      explain(
        [rejection],
        files.map((file) => file.name),
      );
      return;
    }
    const reason = errorMessage(error, t("uploadFailed"));
    const names = files.map((file) => file.name).join(", ");
    toast.error(chatFilePipelineEnabled ? t("uploadPartFailed", { names, reason }) : reason);
  };

  const uploadBatch = async (files: File[]) => {
    const batch = nextBatch++;
    const controller = new AbortController();
    inFlight.current.set(batch, controller);
    const added = files.map((file) => ({ key: `att-${nextKey++}`, name: file.name, mimeType: file.type, batch }));
    setItems((prev) => [...prev, ...added]);
    const run = async () => {
      if (controller.signal.aborted) return;
      let sessionUuid: string;
      try {
        sessionUuid = await ensureSession();
      } catch (error) {
        drop(added.map((a) => a.key));
        toast.error(errorMessage(error, t("uploadFailed")));
        return;
      }
      await uploadInRequests(
        files,
        async (chunk, signal) =>
          (await upload.mutateAsync({ path: { session_uuid: sessionUuid }, body: { files: chunk }, signal })).data,
        (outcome) => {
          const keys = outcome.indices.map((i) => added[i]);
          if ("uploaded" in outcome) {
            // A response with fewer files than sent: the ones it left out are dropped and named, never left "Uploading…"
            const lost = keys.filter((_, i) => !outcome.uploaded[i]?.uuid);
            keys.forEach((a, i) => {
              if (outcome.uploaded[i]?.uuid) update(a.key, { uuid: outcome.uploaded[i].uuid });
            });
            if (lost.length > 0) {
              drop(lost.map((a) => a.key));
              toast.error(t("uploadIncomplete", { names: lost.map((a) => a.name).join(", ") }));
            }
          } else {
            drop(keys.map((a) => a.key));
            reportUploadError(outcome.error, outcome.files);
          }
        },
        {
          split: chatFilePipelineEnabled,
          signal: controller.signal,
          keep: (index) => !removed.current.has(added[index].key),
        },
      );
    };
    const next = queue.current.then(run).finally(() => inFlight.current.delete(batch));
    queue.current = next.catch(() => {});
    await next;
  };

  const add = async (files: File[]) => {
    const { accepted, rejected } = checkAttachments(files, limits);
    explain(rejected);
    await Promise.all(batches(accepted, limits?.maxFiles).map(uploadBatch));
  };

  const addCloud = (files: CloudFile[]) => {
    for (const file of files) {
      const key = `cloud-${nextKey++}`;
      setItems((prev) => [...prev, { key, name: file.name, mimeType: file.mimeType ?? "", cloud: true }]);
      file.load().then(
        (doc) => update(key, { doc }),
        (error) => {
          drop([key]);
          if (error instanceof SharePointFolderLink) {
            setFolders((prev) => [...prev, { key, folder: error.folder }]);
          } else if (error instanceof ApiError && error.code === "sharepoint_is_folder") {
            toast.error(t("folderLink"));
          } else toast.error(errorMessage(error, t("cloudFailed", { name: file.name })));
        },
      );
    }
  };
  const maxFiles = limits?.maxFiles ?? DEFAULT_MAX_FILES;
  const folderLink: FolderLink | null = folders[0]
    ? {
        ...folders[0],
        max: maxFiles,
        room: Math.max(0, maxFiles - items.length),
        done: () => setFolders((prev) => prev.slice(1)),
      }
    : null;

  const { statuses: checks, unknown } = useFileChecks(
    items.flatMap((item) => (item.uuid ? [item.uuid] : [])),
    chatFilePipelineEnabled,
  );
  const check = (item: PendingAttachment): FileCheck => {
    if (!chatFilePipelineEnabled) return "usable";
    return unknown.has(item.uuid ?? "") ? "unknown" : checkOf(checks.get(item.uuid ?? ""));
  };
  // A file whose check can't run or can't be read doesn't hold the message back: the assistant leaves an unchecked
  // file out of the reply (the engine's processing gate), and the composer says so.
  const sendable = (item: PendingAttachment) => ["usable", "unavailable", "unknown"].includes(check(item));
  const ready = (item: PendingAttachment) => Boolean(item.doc) || (Boolean(item.uuid) && sendable(item));

  // A file that failed its check leaves the message, and the person is told why (never dropped silently).
  const unusable = items.filter((item) => item.uuid && check(item) === "unusable");
  useEffect(() => {
    if (unusable.length === 0) return;
    for (const item of unusable) {
      const status = checks.get(item.uuid ?? "");
      if (!status || reported.current.has(status.fileId)) continue;
      reported.current.add(status.fileId);
      toast.error(t("unusable", { name: item.name, reason: t(reasonKey(status)) }));
    }
    drop(unusable.map((item) => item.key));
  });

  const uploaded = items.filter((item) => item.uuid && ready(item));
  const pendingLabel = (item: PendingAttachment) => {
    if (item.cloud) return t("fetching");
    return item.uuid ? t("checking") : t("uploading");
  };
  const detail = (item: PendingAttachment) => {
    if (item.uuid && check(item) === "unavailable") return t("checksUnavailable");
    if (item.uuid && check(item) === "unknown") return t("checkUnknown");
    return ready(item) ? undefined : pendingLabel(item);
  };
  const composerItems: ComposerAttachment[] = items.map((item) => ({
    key: item.key,
    name: item.name,
    mimeType: item.mimeType,
    detail: detail(item),
  }));
  // The chips carry a short status; the full explanation, naming the files, wraps in a notice above the composer
  const named = (state: FileCheck) => items.filter((item) => item.uuid && check(item) === state).map((i) => i.name);
  const checkNotices = [
    ...(named("unavailable").length
      ? [t("notices.checksUnavailable", { names: formatList(named("unavailable")) })]
      : []),
    ...(named("unknown").length ? [t("notices.checkUnknown", { names: formatList(named("unknown")) })] : []),
  ];

  return {
    composerItems,
    /** Translated warnings about files whose checks can't run or can't be read now (shown above the composer). */
    checkNotices,
    /** The file picker's `accept` filter (undefined until the limits load). */
    accept: acceptAttribute(limits),
    add,
    addCloud,
    /** The pasted folder whose files are being offered, if any. */
    folderLink,
    remove,
    clear: () => setItems([]),
    uploading: items.some((item) => !ready(item)),
    /** The message fields that send the attached files. */
    toSend: {
      imageAttachments: uploaded.filter((i) => i.mimeType.startsWith("image/")).map((i) => i.uuid as string),
      documentAttachments: uploaded.filter((i) => !i.mimeType.startsWith("image/")).map((i) => i.uuid as string),
      cloudDocs: items.flatMap((item) => (item.doc ? [item.doc] : [])),
    },
    /** What the message being sent carries, to show on it while the reply is written. */
    sent: items
      .filter((item) => item.doc || (item.uuid && ready(item)))
      .map((item): SentAttachment => ({ key: item.key, name: item.name, mimeType: item.mimeType })),
  };
}
